import { useEffect } from "react";

/**
 * Body class set for the duration of a print. Print rules that have to reach
 * outside the brief — the app's left nav, the card head holding the Export
 * button, the scroll container the brief sits in — are gated on it so they
 * never affect printing from anywhere else in the app.
 */
export const BRIEF_PRINTING_CLASS = "brief-printing";

/** Characters that make for an awkward download filename. */
function toFilenameStem(name: string): string {
    return name
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "")
        .replace(/\s+/g, "-")
        .slice(0, 80);
}

/**
 * Print a brief through the browser's own print pipeline.
 *
 * The export used to be a screenshot (html2canvas via react-to-pdf), which
 * rasterised the page into one tall bitmap and sliced fixed-height bands out of
 * it. That is why exported briefs had no margins and cut text in half: the
 * slice is a pixel row chosen by division, with no idea what is on it. The
 * browser's layout engine paginates properly and honours `@page` and
 * `break-inside`, so the work moves into print CSS and this hook only has to
 * set the stage.
 *
 * Setup hangs off the print EVENT rather than the button's click handler, which
 * means Ctrl-P produces exactly the same document as the button. It also means
 * the class is still applied when the browser re-runs layout for the print
 * preview, which a click handler cannot guarantee.
 *
 * `documentName`, when given, becomes `document.title` for the duration.
 * Browsers propose the title as the filename in their Save-as-PDF dialog, so
 * this is what keeps a recognisable name on the saved file.
 */
export function useBriefPrint(documentName?: string): () => void {
    useEffect(() => {
        const originalTitle = document.title;
        let printing = false;

        const before = () => {
            if (printing) return;
            printing = true;
            document.body.classList.add(BRIEF_PRINTING_CLASS);
            if (documentName) document.title = toFilenameStem(documentName);
        };

        const after = () => {
            if (!printing) return;
            printing = false;
            document.body.classList.remove(BRIEF_PRINTING_CLASS);
            document.title = originalTitle;
        };

        window.addEventListener("beforeprint", before);
        window.addEventListener("afterprint", after);

        // Safari has historically not fired beforeprint/afterprint. A print
        // media-query listener covers it, and firing twice is harmless because
        // both handlers are guarded.
        const printQuery = window.matchMedia?.("print");
        const onQueryChange = (event: MediaQueryListEvent) =>
            event.matches ? before() : after();
        printQuery?.addEventListener?.("change", onQueryChange);

        return () => {
            window.removeEventListener("beforeprint", before);
            window.removeEventListener("afterprint", after);
            printQuery?.removeEventListener?.("change", onQueryChange);
            // Unmounting mid-print (or before any print) must not strand the
            // class or a swapped title on the document.
            after();
        };
    }, [documentName]);

    return () => window.print();
}
